import React, { useState, useEffect } from "react";
import { Navigate } from 'react-router-dom';
import Signup_header from "../Register/signup_header";
import { userService } from "../../_services";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";

export default function CompliancePage() {
    const [supportEmail, setSupportEmail] = useState("support@rxhacker.com");
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userRole, setUserRole] = useState("");

    useEffect(() => {
        // Check if user is logged in
        const hasUserId = !userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"));
        setIsAuthenticated(hasUserId);
        
        if (hasUserId) {
            try {
                const role = window.atob(localStorage.getItem('UserRole'));
                setUserRole(role);
            } catch (error) {
                console.error('Error getting user role:', error);
            }
        }

        // Load support email
        userService.getemail()
            .then(email => setSupportEmail(email))
            .catch(err => console.error('Error loading support email:', err));
    }, []);

    // Redirect non-distributors
    if (isAuthenticated && userRole !== "Distributor") {
        return <Navigate to="/summary" replace />;
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {!isAuthenticated && <Signup_header />}
            
            <div className="container mx-auto px-4 py-8 max-w-5xl">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-3xl font-bold">Compliance Statement</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ScrollArea className="h-[600px] pr-4">
                            <div className="space-y-6 text-sm">
                                <Alert>
                                    <InfoIcon className="h-4 w-4" />
                                    <AlertDescription>
                                        NuLifeSpan Rx is committed to legal and ethical business practices. Compliance is everyone's responsibility!
                                    </AlertDescription>
                                </Alert>

                                <p className="leading-relaxed">
                                    NuLifeSpan Rx ("we" or "us") is committed to legal and ethical business practices and it takes compliance with healthcare laws very seriously. But it doesn't stop with us. Compliance is everyone's responsibility! To this end, we require all personnel, vendors, contractors, and partners to review the following summary of key compliance issues related to our business offerings:
                                </p>
                                
                                <div className="space-y-4">
                                    <div className="border-l-4 border-primary pl-4">
                                        <h3 className="font-semibold mb-2">Ethical Business Conduct</h3>
                                        <p className="mb-2">NuLifeSpan Rx expects you to conduct yourself in an ethical and legal manner. It's about doing the right thing!</p>
                                        <ul className="list-disc ml-6 space-y-1">
                                            <li>Act fairly and honestly</li>
                                            <li>Adhere to high ethical standards in all that you do</li>
                                            <li>Comply with all applicable laws and regulations</li>
                                            <li>Report suspected violations to <a href={`mailto:${supportEmail}`} className="text-primary hover:underline">{supportEmail}</a></li>
                                        </ul>
                                    </div>

                                    <div className="border-l-4 border-primary pl-4">
                                        <h3 className="font-semibold mb-2">Anti-Kickback Statute (AKS)</h3>
                                        <p className="mb-2">
                                            The federal Anti-Kickback Statute (AKS) is a criminal statute that prohibits the exchange (or offer to exchange), of anything of value, in an effort to induce (or reward) the referral of business reimbursable by federal health care programs.
                                        </p>
                                        <p className="font-semibold text-destructive">What this means for you:</p>
                                        <ul className="list-disc ml-6 space-y-1">
                                            <li>You can NOT offer anything of value to induce or reward referrals of federal health care program business</li>
                                            <li>You can NOT offer kickbacks or bribes in any form</li>
                                            <li>Violations of the AKS can result in criminal prosecution, civil penalties, and exclusion from participation in federal health care programs</li>
                                        </ul>
                                    </div>

                                    <div className="border-l-4 border-destructive pl-4 bg-destructive/5 p-3 rounded-r">
                                        <h3 className="font-semibold mb-2 text-destructive">Prohibition on Healthcare Provider Involvement</h3>
                                        <p className="mb-2">
                                            <strong>You may NOT:</strong>
                                        </p>
                                        <ul className="list-disc ml-6 space-y-1">
                                            <li>Hire or contract with doctors, nurses, pharmacists, or pharmacy technicians to market our products</li>
                                            <li>Offer any payments, gifts, or incentives to healthcare providers or pharmacy employees</li>
                                            <li>Share commissions with healthcare providers</li>
                                            <li>Ask healthcare providers to promote the program in exchange for payment</li>
                                        </ul>
                                    </div>

                                    <div className="border-l-4 border-primary pl-4">
                                        <h3 className="font-semibold mb-2">Marketing Material Pre-Approval</h3>
                                        <p className="mb-2">
                                            ALL marketing materials MUST be approved by NuLifeSpan Rx before use, including:
                                        </p>
                                        <ul className="list-disc ml-6 space-y-1">
                                            <li>Websites</li>
                                            <li>Social media posts</li>
                                            <li>Prescription cards</li>
                                            <li>Flyers and brochures</li>
                                            <li>Articles and mailers</li>
                                            <li>Media broadcasts</li>
                                        </ul>
                                        <p className="mt-2 font-semibold">
                                            Failure to obtain pre-approval is a material breach of your contract.
                                        </p>
                                    </div>

                                    <div className="border-l-4 border-primary pl-4">
                                        <h3 className="font-semibold mb-2">Reporting Requirements</h3>
                                        <p className="mb-2">You MUST immediately report:</p>
                                        <ul className="list-disc ml-6 space-y-1">
                                            <li>Any suspected violations of these compliance requirements</li>
                                            <li>Requests from healthcare providers for payments or kickbacks</li>
                                            <li>Use of unapproved marketing materials</li>
                                            <li>Any illegal or unethical behavior related to the program</li>
                                        </ul>
                                        <p className="mt-2">
                                            Report to: <a href={`mailto:${supportEmail}`} className="text-primary hover:underline">{supportEmail}</a>
                                        </p>
                                    </div>

                                    <div className="border-l-4 border-primary pl-4">
                                        <h3 className="font-semibold mb-2">Important Certifications</h3>
                                        <p className="mb-2">As a contractor, you certify that:</p>
                                        <ul className="list-disc ml-6 space-y-1">
                                            <li>You are NOT a healthcare provider (doctor, nurse, pharmacist, pharmacy technician)</li>
                                            <li>You have never been excluded from any Federal Health Care Program</li>
                                            <li>You will comply with all applicable laws and regulations</li>
                                            <li>You will ensure all your subcontractors also comply with these requirements</li>
                                        </ul>
                                    </div>

                                    <div className="border-l-4 border-amber-500 pl-4 bg-amber-50 p-3 rounded-r">
                                        <h3 className="font-semibold mb-2">Consequences of Non-Compliance</h3>
                                        <ul className="list-disc ml-6 space-y-1">
                                            <li>Immediate contract termination</li>
                                            <li>Loss of commissions</li>
                                            <li>Criminal prosecution</li>
                                            <li>Civil penalties and fines</li>
                                            <li>Exclusion from federal health care programs</li>
                                        </ul>
                                    </div>
                                </div>

                                <Alert className="mt-6">
                                    <InfoIcon className="h-4 w-4" />
                                    <AlertDescription>
                                        <strong>Remember:</strong> When in doubt, ask! It's better to seek guidance than to risk non-compliance. 
                                        Contact us at <a href={`mailto:${supportEmail}`} className="text-primary hover:underline">{supportEmail}</a>
                                    </AlertDescription>
                                </Alert>
                            </div>
                        </ScrollArea>
                    </CardContent>
                </Card>
            </div>
            
            <footer className="bg-white border-t py-6 mt-8">
                <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
                    <div className="mb-2">
                        <a href="/terms" className="hover:text-primary transition-colors">Terms</a>
                        {" | "}
                        <a href="https://rxhacker.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Privacy</a>
                    </div>
                    <div>
                        Copyright © {new Date().getFullYear()} NuLifeSpan Rx. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
}
